import { useEffect, useState, useCallback, useRef } from "react";
import api, { API, formatError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Film, Loader2, Plus, Trash2, Wand2, ImageUp, Check, RefreshCw, ArrowUp, ArrowDown,
  Clapperboard, Download, AlertTriangle, ChevronLeft, FolderOpen, Sparkles, Lock, Type, Send, Tv,
} from "lucide-react";
import { Dialog } from "../components/ui/dialog";
import { PushDialog } from "./AiSlides";

const STYLES = ["Cinematic", "Realistic", "Animation", "Commercial"];
const ASPECTS = [
  { v: "16:9", label: "16:9 (wide)" },
  { v: "9:16", label: "9:16 (vertical)" },
  { v: "1:1", label: "1:1 (square)" },
];
const LENGTHS = [15, 30, 45];

const kfUrl = (p, s) => `${API}/director/projects/${p}/shots/${s.id}/keyframe?v=${s.keyframe_path || 0}`;
const clipUrl = (p, s) => `${API}/director/projects/${p}/shots/${s.id}/clip?v=${s.clip_path || 0}`;
const finalUrl = (p, fp) => `${API}/director/projects/${p}/final?v=${fp || 0}`;

const aspectBox = (aspect) =>
  aspect === "9:16" ? "aspect-[9/16] max-w-[220px] mx-auto" : aspect === "1:1" ? "aspect-square" : "aspect-video";

// ---------- Step 1: new project ----------
function NewProjectForm({ onCreated }) {
  const [idea, setIdea] = useState("");
  const [style, setStyle] = useState("Cinematic");
  const [aspect, setAspect] = useState("16:9");
  const [length, setLength] = useState("30");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();
  const [refName, setRefName] = useState("");

  const create = async () => {
    if (!idea.trim()) return toast.error("Describe your idea first");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("idea", idea.trim());
      fd.append("style", style);
      fd.append("aspect_ratio", aspect);
      fd.append("total_length", length);
      const f = fileRef.current?.files?.[0];
      if (f) fd.append("reference", f);
      const { data: proj } = await api.post("/director/projects", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 });
      toast.success("Project created — writing your shot list…");
      const { data: withBrief } = await api.post(`/director/projects/${proj.id}/brief`, { note: "" }, { timeout: 90000 });
      onCreated(withBrief);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Could not start the project");
    }
    setBusy(false);
  };

  return (
    <div className="rounded-xl border border-border bg-card p-5 space-y-4" data-testid="director-new-form">
      <div className="flex items-center gap-2">
        <Wand2 className="w-5 h-5 text-primary" />
        <h2 className="text-lg font-semibold">New video — Step 1: your idea</h2>
      </div>
      <div>
        <Label>What's the video about?</Label>
        <Textarea value={idea} onChange={(e) => setIdea(e.target.value)} rows={3} className="mt-1 resize-none"
          data-testid="director-idea-input"
          placeholder="e.g. a 30-second ad for our new cold-brew coffee, energetic morning vibe in a busy café" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <Label>Style</Label>
          <Select value={style} onValueChange={setStyle}>
            <SelectTrigger className="mt-1" data-testid="director-style-select"><SelectValue /></SelectTrigger>
            <SelectContent>{STYLES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label>Aspect ratio</Label>
          <Select value={aspect} onValueChange={setAspect}>
            <SelectTrigger className="mt-1" data-testid="director-aspect-select"><SelectValue /></SelectTrigger>
            <SelectContent>{ASPECTS.map((a) => <SelectItem key={a.v} value={a.v}>{a.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label>Total length</Label>
          <Select value={length} onValueChange={setLength}>
            <SelectTrigger className="mt-1" data-testid="director-length-select"><SelectValue /></SelectTrigger>
            <SelectContent>{LENGTHS.map((l) => <SelectItem key={l} value={String(l)}>{l} sec</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <Label>Reference image (optional)</Label>
        <div className="mt-1 flex items-center gap-2">
          <input ref={fileRef} type="file" accept="image/*" hidden data-testid="director-ref-input"
            onChange={(e) => setRefName(e.target.files?.[0]?.name || "")} />
          <Button type="button" variant="secondary" size="sm" onClick={() => fileRef.current?.click()}>
            <ImageUp className="w-4 h-4 mr-1.5" /> Choose image
          </Button>
          {refName && <span className="text-xs text-muted-foreground truncate">{refName}</span>}
        </div>
      </div>
      <Button onClick={create} disabled={busy} className="w-full" data-testid="director-create-btn">
        {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating the brief…</> : <><Sparkles className="w-4 h-4 mr-2" /> Generate brief</>}
      </Button>
    </div>
  );
}

// ---------- Stepper ----------
function Stepper({ step, onJump, project }) {
  const steps = ["Idea", "Brief", "Keyframes", "Video"];
  const reached = (i) => (project?.step || 1) >= i + 1 || step >= i + 1;
  return (
    <div className="flex items-center gap-2 mb-5" data-testid="director-stepper">
      {steps.map((s, i) => (
        <button key={s} type="button" disabled={!reached(i)} onClick={() => reached(i) && onJump(i + 1)}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
            step === i + 1 ? "bg-primary text-primary-foreground" : reached(i) ? "bg-secondary text-foreground hover:bg-secondary/80" : "bg-secondary/40 text-muted-foreground cursor-not-allowed"
          }`} data-testid={`director-step-${i + 1}`}>
          <span className="w-5 h-5 rounded-full bg-black/20 flex items-center justify-center text-[10px]">{i + 1}</span>
          {s}
        </button>
      ))}
    </div>
  );
}

// ---------- Step 2: Brief ----------
function BriefStep({ project, setProject, onNext }) {
  const [shots, setShots] = useState(project.shots || []);
  const [logline, setLogline] = useState(project.logline || "");
  const [lookLock, setLookLock] = useState(project.look_lock || "");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setShots(project.shots || []); setLogline(project.logline || ""); setLookLock(project.look_lock || ""); }, [project]);

  const upd = (i, field, v) => setShots((prev) => prev.map((s, idx) => (idx === i ? { ...s, [field]: v } : s)));
  const move = (i, dir) => setShots((prev) => {
    const j = i + dir; if (j < 0 || j >= prev.length) return prev;
    const copy = [...prev]; [copy[i], copy[j]] = [copy[j], copy[i]]; return copy;
  });
  const del = (i) => setShots((prev) => prev.filter((_, idx) => idx !== i));
  const add = () => setShots((prev) => prev.length >= 6 ? prev : [...prev, {
    id: `new-${Date.now()}`, description: "", camera: "", lighting: "", action: "", voiceover: "", duration: 8,
    keyframe_status: "none", keyframe_approved: false, clip_status: "none", clip_approved: false,
  }]);

  const persist = async (extra = {}) => {
    const { data } = await api.put(`/director/projects/${project.id}`, { shots, logline, look_lock: lookLock, ...extra });
    setProject(data); return data;
  };

  const save = async () => { setSaving(true); try { await persist(); toast.success("Saved"); } catch (e) { toast.error("Could not save"); } setSaving(false); };

  const regen = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/director/projects/${project.id}/brief`, { note }, { timeout: 90000 });
      setProject(data); setNote(""); toast.success("Brief regenerated");
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || "Regenerate failed"); }
    setBusy(false);
  };

  const next = async () => { setSaving(true); try { await persist({ step: 3 }); onNext(3); } catch (e) { toast.error("Could not save"); } setSaving(false); };

  return (
    <div className="space-y-4" data-testid="director-brief-step">
      {logline !== undefined && (
        <div className="rounded-lg border border-border bg-card p-4 space-y-3">
          <div>
            <Label>Logline</Label>
            <Input value={logline} onChange={(e) => setLogline(e.target.value)} className="mt-1" data-testid="director-logline-input" />
          </div>
          <div>
            <Label className="flex items-center gap-1.5"><Lock className="w-3.5 h-3.5" /> Look lock <span className="text-[10px] font-normal text-muted-foreground">(prepended to every shot for consistency)</span></Label>
            <Textarea value={lookLock} onChange={(e) => setLookLock(e.target.value)} rows={3} className="mt-1 resize-none" data-testid="director-looklock-input" />
          </div>
        </div>
      )}

      <div className="space-y-3">
        {shots.map((s, i) => (
          <div key={s.id} className="rounded-lg border border-border bg-card p-4" data-testid={`director-shot-card-${i}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-primary">Shot {i + 1}</span>
              <div className="flex items-center gap-1">
                <Button size="sm" variant="ghost" className="h-7 w-7 p-0" disabled={i === 0} onClick={() => move(i, -1)} data-testid={`director-shot-up-${i}`}><ArrowUp className="w-3.5 h-3.5" /></Button>
                <Button size="sm" variant="ghost" className="h-7 w-7 p-0" disabled={i === shots.length - 1} onClick={() => move(i, 1)} data-testid={`director-shot-down-${i}`}><ArrowDown className="w-3.5 h-3.5" /></Button>
                <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive" onClick={() => del(i)} data-testid={`director-shot-del-${i}`}><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            </div>
            <div className="space-y-2">
              <Textarea value={s.description} onChange={(e) => upd(i, "description", e.target.value)} rows={2} className="resize-none text-sm" placeholder="Shot description" data-testid={`director-shot-desc-${i}`} />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <Input value={s.camera} onChange={(e) => upd(i, "camera", e.target.value)} placeholder="Camera (angle / movement)" className="text-sm" data-testid={`director-shot-camera-${i}`} />
                <Input value={s.lighting} onChange={(e) => upd(i, "lighting", e.target.value)} placeholder="Lighting / mood" className="text-sm" />
                <Input value={s.action} onChange={(e) => upd(i, "action", e.target.value)} placeholder="Action" className="text-sm" />
                <Input value={s.voiceover} onChange={(e) => upd(i, "voiceover", e.target.value)} placeholder="Voiceover (optional)" className="text-sm" />
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-xs">Duration</Label>
                <Select value={String(s.duration)} onValueChange={(v) => upd(i, "duration", Number(v))}>
                  <SelectTrigger className="h-8 w-24 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>{[5, 6, 7, 8].map((d) => <SelectItem key={d} value={String(d)}>{d} sec</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-2">
        <Button variant="secondary" size="sm" onClick={add} disabled={shots.length >= 6} data-testid="director-add-shot-btn"><Plus className="w-4 h-4 mr-1.5" /> Add shot ({shots.length}/6)</Button>
        <Button variant="secondary" size="sm" onClick={save} disabled={saving} data-testid="director-save-brief-btn">{saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Check className="w-4 h-4 mr-1.5" />} Save</Button>
      </div>

      <div className="rounded-lg border border-border bg-secondary/30 p-3 flex flex-col sm:flex-row gap-2 sm:items-end">
        <div className="flex-1">
          <Label className="text-xs">Regenerate brief (optional note)</Label>
          <Input value={note} onChange={(e) => setNote(e.target.value)} className="mt-1" placeholder="e.g. make it punchier, add a product close-up" data-testid="director-regen-note" />
        </div>
        <Button variant="secondary" onClick={regen} disabled={busy} data-testid="director-regen-brief-btn">{busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1.5" />} Regenerate</Button>
      </div>

      <Button onClick={next} disabled={saving || shots.length === 0} className="w-full" data-testid="director-to-keyframes-btn">Next: Keyframes →</Button>
    </div>
  );
}

// ---------- Step 3: Keyframes ----------
function KeyframeStep({ project, setProject, onNext }) {
  const [busyShot, setBusyShot] = useState(null);
  const [notes, setNotes] = useState({});
  const [genAll, setGenAll] = useState(false);
  const shots = project.shots || [];
  const allApproved = shots.length > 0 && shots.every((s) => s.keyframe_approved);

  const genOne = async (sid, note = "") => {
    setBusyShot(sid);
    try {
      const { data } = await api.post(`/director/projects/${project.id}/shots/${sid}/keyframe`, { note }, { timeout: 120000 });
      setProject(data);
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || "Keyframe failed"); throw e; }
    finally { setBusyShot(null); }
  };

  const generateAll = async () => {
    setGenAll(true);
    for (const s of shots) {
      if (s.keyframe_status !== "ready") {
        try { await genOne(s.id); } catch { break; }
      }
    }
    setGenAll(false);
  };

  const approve = async (sid, val) => {
    const newShots = shots.map((s) => (s.id === sid ? { ...s, keyframe_approved: val } : s));
    try { const { data } = await api.put(`/director/projects/${project.id}`, { shots: newShots }); setProject(data); }
    catch { toast.error("Could not update"); }
  };

  return (
    <div className="space-y-4" data-testid="director-keyframe-step">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">Generate one still per shot, then approve each. Video stays locked until all are approved — so you never waste credits on a bad shot.</p>
        <Button onClick={generateAll} disabled={genAll} data-testid="director-gen-keyframes-btn">{genAll ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Generating…</> : <><ImageUp className="w-4 h-4 mr-1.5" /> Generate keyframes</>}</Button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {shots.map((s, i) => (
          <div key={s.id} className={`rounded-xl border bg-card overflow-hidden ${s.keyframe_approved ? "border-emerald-500/50" : "border-border"}`} data-testid={`director-kf-card-${i}`}>
            <div className={`bg-black flex items-center justify-center ${aspectBox(project.aspect_ratio)}`}>
              {s.keyframe_status === "ready" ? (
                <img src={kfUrl(project.id, s)} alt={`shot ${i + 1}`} className="w-full h-full object-contain" data-testid={`director-kf-img-${i}`} />
              ) : busyShot === s.id ? (
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              ) : (
                <div className="text-center text-muted-foreground text-xs px-3"><ImageUp className="w-6 h-6 mx-auto mb-1.5 opacity-50" />Not generated yet</div>
              )}
            </div>
            <div className="p-3 space-y-2">
              <p className="text-xs font-semibold text-primary">Shot {i + 1}</p>
              <p className="text-xs text-muted-foreground line-clamp-2">{s.description}</p>
              {s.keyframe_status === "ready" && (
                <div className="flex items-center gap-2">
                  <Button size="sm" className={`flex-1 h-8 text-xs ${s.keyframe_approved ? "bg-emerald-600 hover:bg-emerald-700" : ""}`} onClick={() => approve(s.id, !s.keyframe_approved)} data-testid={`director-kf-approve-${i}`}>
                    <Check className="w-3.5 h-3.5 mr-1" /> {s.keyframe_approved ? "Approved" : "Approve"}
                  </Button>
                </div>
              )}
              <div className="flex items-center gap-2">
                <Input value={notes[s.id] || ""} onChange={(e) => setNotes((n) => ({ ...n, [s.id]: e.target.value }))} placeholder="note for regenerate" className="h-8 text-xs" data-testid={`director-kf-note-${i}`} />
                <Button size="sm" variant="secondary" className="h-8 px-2" disabled={busyShot === s.id} onClick={() => genOne(s.id, notes[s.id] || "")} data-testid={`director-kf-regen-${i}`}>
                  {busyShot === s.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
      <Button onClick={() => onNext(4)} disabled={!allApproved} className="w-full" data-testid="director-to-video-btn">
        {allApproved ? "Next: Generate video →" : <><Lock className="w-4 h-4 mr-1.5" /> Approve all keyframes to unlock video</>}
      </Button>
    </div>
  );
}

// ---------- Step 4: Video ----------
function VideoStep({ project, setProject, tvs }) {
  const [notes, setNotes] = useState({});
  const [stitching, setStitching] = useState(false);
  const [sending, setSending] = useState(false);
  const [pushMedia, setPushMedia] = useState(null);
  const [headline, setHeadline] = useState(project.caption_headline || "");
  const [price, setPrice] = useState(project.caption_price || "");
  const [halal, setHalal] = useState(project.halal_badges || false);
  const shots = project.shots || [];
  const anyProcessing = shots.some((s) => s.clip_status === "processing") || project.final_status === "processing";
  const allReadyApproved = shots.length > 0 && shots.every((s) => s.clip_status === "ready" && s.clip_approved);

  // poll while anything is rendering
  useEffect(() => {
    if (!anyProcessing) return;
    const t = setInterval(async () => {
      try { const { data } = await api.get(`/director/projects/${project.id}`); setProject(data); } catch { /* keep trying */ }
    }, 4000);
    return () => clearInterval(t);
  }, [anyProcessing, project.id, setProject]);

  const genClip = async (sid, note = "") => {
    try {
      const { data } = await api.post(`/director/projects/${project.id}/shots/${sid}/clip`, { note }, { timeout: 120000 });
      setProject(data);
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || "Could not start clip"); }
  };

  const genAll = async () => {
    for (const s of shots) {
      if (s.clip_status === "none" || s.clip_status === "failed") await genClip(s.id);
    }
  };

  const approve = async (sid, val) => {
    const newShots = shots.map((s) => (s.id === sid ? { ...s, clip_approved: val } : s));
    try { const { data } = await api.put(`/director/projects/${project.id}`, { shots: newShots }); setProject(data); }
    catch { toast.error("Could not update"); }
  };

  const stitch = async () => {
    setStitching(true);
    try { await api.post(`/director/projects/${project.id}/stitch`, { caption_headline: headline, caption_price: price, halal_badges: halal }); const { data } = await api.get(`/director/projects/${project.id}`); setProject(data); toast.success("Stitching your final video…"); }
    catch (e) { toast.error(formatError(e.response?.data?.detail) || "Could not stitch"); }
    setStitching(false);
  };

  const sendToTv = async () => {
    setSending(true);
    try {
      const { data: media } = await api.post(`/director/projects/${project.id}/send-to-library`);
      setPushMedia(media);
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || "Could not prepare the video"); }
    setSending(false);
  };

  return (
    <div className="space-y-4" data-testid="director-video-step">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">Generate a clip per shot from its approved keyframe. Approve each clip, then stitch them into one video.</p>
        <Button onClick={genAll} data-testid="director-gen-clips-btn"><Clapperboard className="w-4 h-4 mr-1.5" /> Generate all clips</Button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {shots.map((s, i) => (
          <div key={s.id} className={`rounded-xl border bg-card overflow-hidden ${s.clip_approved ? "border-emerald-500/50" : "border-border"}`} data-testid={`director-clip-card-${i}`}>
            <div className={`bg-black flex items-center justify-center ${aspectBox(project.aspect_ratio)}`}>
              {s.clip_status === "ready" ? (
                <video src={clipUrl(project.id, s)} className="w-full h-full object-contain" muted loop autoPlay playsInline data-testid={`director-clip-video-${i}`} />
              ) : s.clip_status === "processing" ? (
                <div className="text-center text-muted-foreground text-xs"><Loader2 className="w-6 h-6 mx-auto mb-1.5 animate-spin text-primary" />Rendering… ~1–3 min</div>
              ) : s.clip_status === "failed" ? (
                <div className="text-center text-destructive text-xs px-3"><AlertTriangle className="w-6 h-6 mx-auto mb-1.5" />{s.clip_error || "Failed"}</div>
              ) : (
                <img src={kfUrl(project.id, s)} alt="" className="w-full h-full object-contain opacity-60" />
              )}
            </div>
            <div className="p-3 space-y-2">
              <p className="text-xs font-semibold text-primary">Shot {i + 1}</p>
              {s.clip_status === "ready" && (
                <Button size="sm" className={`w-full h-8 text-xs ${s.clip_approved ? "bg-emerald-600 hover:bg-emerald-700" : ""}`} onClick={() => approve(s.id, !s.clip_approved)} data-testid={`director-clip-approve-${i}`}>
                  <Check className="w-3.5 h-3.5 mr-1" /> {s.clip_approved ? "Approved" : "Approve"}
                </Button>
              )}
              {(s.clip_status === "none" || s.clip_status === "ready" || s.clip_status === "failed") && (
                <div className="flex items-center gap-2">
                  <Input value={notes[s.id] || ""} onChange={(e) => setNotes((n) => ({ ...n, [s.id]: e.target.value }))} placeholder={s.clip_status === "none" ? "" : "note for regenerate"} className="h-8 text-xs" data-testid={`director-clip-note-${i}`} />
                  <Button size="sm" variant="secondary" className="h-8 px-2.5 text-xs whitespace-nowrap" onClick={() => genClip(s.id, notes[s.id] || "")} data-testid={`director-clip-gen-${i}`}>
                    {s.clip_status === "none" ? "Generate" : <RefreshCw className="w-3.5 h-3.5" />}
                  </Button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="rounded-xl border border-border bg-card p-4 space-y-3" data-testid="director-caption-card">
        <div className="flex items-center gap-2">
          <Type className="w-4 h-4 text-primary" />
          <h3 className="text-sm font-semibold">On-screen text (prices & offers)</h3>
        </div>
        <p className="text-[11px] text-muted-foreground -mt-1">Burned onto the final video as a lower-third caption, so customers see your message on the TV.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Headline (optional)</Label>
            <Input value={headline} onChange={(e) => setHeadline(e.target.value)} maxLength={60} placeholder="e.g. WEEKEND SPECIAL" className="mt-1" data-testid="director-caption-headline" />
          </div>
          <div>
            <Label className="text-xs">Price line (optional)</Label>
            <Input value={price} onChange={(e) => setPrice(e.target.value)} maxLength={40} placeholder="e.g. Fresh Lamb $6.99/lb" className="mt-1" data-testid="director-caption-price" />
          </div>
        </div>
        <button type="button" onClick={() => setHalal((v) => !v)} data-testid="director-halal-toggle"
          className={`w-full flex items-center gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors ${halal ? "border-primary bg-primary/10" : "border-border hover:border-primary/50"}`}>
          <span className={`flex h-5 w-5 items-center justify-center rounded border flex-shrink-0 ${halal ? "bg-primary border-primary text-primary-foreground" : "border-muted-foreground/40"}`}>
            {halal && <Check className="w-3.5 h-3.5" />}
          </span>
          <span className="min-w-0">
            <span className="block text-sm font-medium">Show HMS + Zabiha Halal badges</span>
            <span className="block text-[11px] text-muted-foreground">Adds the halal certification logos in the corner — recommended for meat.</span>
          </span>
        </button>
      </div>

      {project.final_status === "ready" && project.final_path ? (
        <div className="rounded-xl border border-emerald-500/40 bg-card p-4 space-y-3" data-testid="director-final-ready">
          <h3 className="font-semibold flex items-center gap-2"><Check className="w-5 h-5 text-emerald-400" /> Final video ready</h3>
          <div className={`bg-black rounded-lg overflow-hidden ${aspectBox(project.aspect_ratio)}`}>
            <video src={finalUrl(project.id, project.final_path)} className="w-full h-full object-contain" controls autoPlay loop playsInline data-testid="director-final-video" />
          </div>
          <a href={finalUrl(project.id, project.final_path)} download>
            <Button variant="secondary" className="w-full" data-testid="director-download-btn"><Download className="w-4 h-4 mr-1.5" /> Download MP4</Button>
          </a>
          <Button className="w-full" onClick={sendToTv} disabled={sending} data-testid="director-send-tv-btn">
            {sending ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Tv className="w-4 h-4 mr-1.5" />} Send to TV
          </Button>
          <Button variant="secondary" className="w-full" onClick={stitch} disabled={stitching} data-testid="director-restitch-btn">
            {stitching ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Type className="w-4 h-4 mr-1.5" />} Re-make with the text above
          </Button>
        </div>
      ) : project.final_status === "processing" ? (
        <div className="rounded-xl border border-border bg-card p-6 text-center" data-testid="director-final-processing">
          <Loader2 className="w-6 h-6 mx-auto mb-2 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Stitching your final video…</p>
        </div>
      ) : (
        <div className="space-y-2">
          {project.final_status === "failed" && <p className="text-xs text-destructive flex items-center gap-1.5" data-testid="director-final-failed"><AlertTriangle className="w-3.5 h-3.5" /> {project.final_error || "Stitch failed"}. Try again.</p>}
          <Button onClick={stitch} disabled={!allReadyApproved || stitching} className="w-full" data-testid="director-stitch-btn">
            {stitching ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Film className="w-4 h-4 mr-1.5" />}
            {allReadyApproved ? "Stitch final video" : "Approve every clip to stitch"}
          </Button>
        </div>
      )}

      <Dialog open={!!pushMedia} onOpenChange={(o) => !o && setPushMedia(null)}>
        {pushMedia && <PushDialog slide={pushMedia} tvs={tvs || []} onClose={() => setPushMedia(null)} onPushed={() => {}} />}
      </Dialog>
    </div>
  );
}

// ---------- Page ----------
export default function AiDirector() {
  const [projects, setProjects] = useState([]);
  const [active, setActive] = useState(null);
  const [view, setView] = useState(1);
  const [creating, setCreating] = useState(false);
  const [tvs, setTvs] = useState([]);

  const loadProjects = useCallback(() => api.get("/director/projects").then((r) => setProjects(r.data)).catch(() => {}), []);
  useEffect(() => { loadProjects(); api.get("/tvs").then((r) => setTvs(r.data)).catch(() => {}); }, [loadProjects]);

  const openProject = async (id) => {
    try { const { data } = await api.get(`/director/projects/${id}`); setActive(data); setView(data.step || 2); setCreating(false); }
    catch { toast.error("Could not open project"); }
  };

  const onCreated = (proj) => { setActive(proj); setView(2); setCreating(false); loadProjects(); };

  const closeProject = () => { setActive(null); setCreating(false); setView(1); loadProjects(); };

  const delProject = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this project?")) return;
    try { await api.delete(`/director/projects/${id}`); if (active?.id === id) closeProject(); loadProjects(); toast.success("Deleted"); }
    catch { toast.error("Could not delete"); }
  };

  const setProject = (p) => { setActive(p); };

  return (
    <div className="p-4 md:p-8 pb-24 md:pb-8 max-w-4xl">
      <div className="mb-5 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl md:text-3xl font-display font-bold tracking-tight flex items-center gap-2">
            <Film className="w-6 h-6 text-primary" /> AI Video Director
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">Type an idea and direct the AI shot by shot — brief, keyframes, then video — so the final cut matches what you imagined.</p>
        </div>
        {(active || creating) && (
          <Button variant="secondary" size="sm" onClick={closeProject} data-testid="director-back-btn"><ChevronLeft className="w-4 h-4 mr-1" /> Projects</Button>
        )}
      </div>

      {/* My projects */}
      {!active && !creating && (
        <div className="space-y-4" data-testid="director-home">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold flex items-center gap-1.5"><FolderOpen className="w-4 h-4 text-muted-foreground" /> My projects</h2>
            <Button size="sm" onClick={() => setCreating(true)} data-testid="director-new-btn"><Plus className="w-4 h-4 mr-1.5" /> New video</Button>
          </div>
          {projects.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border p-10 text-center" data-testid="director-empty">
              <Film className="w-10 h-10 mx-auto mb-3 text-muted-foreground/40" />
              <p className="text-muted-foreground">No projects yet. Click <b>New video</b> to direct your first one.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {projects.map((p) => (
                <button key={p.id} onClick={() => openProject(p.id)} className="text-left rounded-xl border border-border bg-card p-4 hover:border-primary/50 transition-colors" data-testid={`director-project-${p.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-semibold text-sm line-clamp-2">{p.title || p.idea}</p>
                    <span onClick={(e) => delProject(p.id, e)} className="text-muted-foreground hover:text-destructive flex-shrink-0" data-testid={`director-project-del-${p.id}`}><Trash2 className="w-4 h-4" /></span>
                  </div>
                  <div className="mt-2 flex items-center gap-2 text-[11px] text-muted-foreground">
                    <span className="px-1.5 py-0.5 rounded bg-secondary">{p.style}</span>
                    <span className="px-1.5 py-0.5 rounded bg-secondary">{p.aspect_ratio}</span>
                    <span className="px-1.5 py-0.5 rounded bg-secondary">{p.total_length}s</span>
                  </div>
                  <p className="mt-2 text-[11px] text-primary capitalize">{p.status} · step {p.step}</p>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* New project */}
      {creating && !active && <NewProjectForm onCreated={onCreated} />}

      {/* Active project editor */}
      {active && (
        <div data-testid="director-editor">
          <Stepper step={view} onJump={setView} project={active} />
          <div className="mb-4 rounded-lg border border-border bg-secondary/20 px-3 py-2 text-xs text-muted-foreground">
            <b className="text-foreground">{active.title || active.idea}</b> · {active.style} · {active.aspect_ratio} · {active.total_length}s
          </div>
          {view === 2 && <BriefStep project={active} setProject={setProject} onNext={setView} />}
          {view === 3 && <KeyframeStep project={active} setProject={setProject} onNext={setView} />}
          {view === 4 && <VideoStep project={active} setProject={setProject} tvs={tvs} />}
          {view === 1 && <p className="text-sm text-muted-foreground">This project's idea is locked. Use the Brief step to edit shots.</p>}
        </div>
      )}
    </div>
  );
}
